import { useState, useEffect, useCallback } from 'react';

/**
 * SSR 호환 미디어 쿼리 훅
 * 서버사이드 렌더링과 클라이언트 사이드 모두에서 작동하는 미디어 쿼리 훅
 * 
 * @param {string} query - CSS 미디어 쿼리 문자열 (예: '(max-width: 767px)')
 * @param {boolean} defaultValue - SSR 시 사용할 기본값 (선택사항)
 * @returns {boolean} 미디어 쿼리 일치 여부
 */
const useMediaQuery = (query, defaultValue = false) => {
  // SSR 시 기본값 사용, 클라이언트에서는 실제 미디어 쿼리 결과 사용
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return defaultValue;
    return window.matchMedia(query).matches;
  });

  const handleChange = useCallback((event) => {
    setMatches(event.matches);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);
    
    // 현재 상태와 다를 때만 업데이트 (불필요한 리렌더링 방지)
    if (mediaQuery.matches !== matches) {
      setMatches(mediaQuery.matches);
    }

    // 최신 브라우저는 addEventListener를 지원하므로 우선 사용
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query, handleChange, matches]);

  return matches;
};

export default useMediaQuery;
